import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { VacationRequest } from '@domain/entities/VacationRequest';
import { getServices } from '@infrastructure/services';
import { diffRequests, type RequestChanges } from './requestChanges';

/**
 * A live view of some slice of the requests, and what changed in it.
 *
 * One hook for both sides of the desk. The employee subscribes to their own
 * requests and hears when a leader decides one; the leader subscribes to their
 * queue and hears when one arrives. The only difference is the subscription,
 * which the caller supplies.
 *
 * **The poll is not a nicety.** This rides Firestore's streaming channel, and
 * networks exist that block it while ordinary reads go through — the one this
 * portal runs on is one of them. Without the fallback, «se actualiza solo» would
 * be true everywhere except the place it was demonstrated. On that path changes
 * arrive late rather than never, and nothing announces itself: a toast about
 * something that happened four minutes ago is worse than a screen that quietly
 * caught up, so `onChanges` only fires for deliveries the live channel made.
 */

/** How often to re-read when the live channel is unusable. */
const POLL_MS = 20_000;

export type Subscribe = (
  onChange: (requests: readonly VacationRequest[]) => void,
  onError: (error: unknown) => void,
) => () => void;

export const useLiveRequests = (options: {
  /** Null switches the subscription off — no scope, no session, nothing to watch. */
  readonly subscribe: Subscribe | null;
  /** Query families to invalidate when something moves. */
  readonly invalidate: readonly (readonly string[])[];
  /** Called only for live deliveries after the first. */
  readonly onChanges?: (changes: RequestChanges) => void;
}): void => {
  const queryClient = useQueryClient();

  /*
   * The callback in a ref, and the effect keyed on the subscription alone.
   *
   * Callers pass an inline arrow — every render would otherwise tear the
   * subscription down and open a new one, which on a listener means a fresh
   * read of the whole result set each time and a first delivery that looks like
   * news all over again.
   */
  const onChanges = useRef(options.onChanges);
  onChanges.current = options.onChanges;

  const invalidate = useRef(options.invalidate);
  invalidate.current = options.invalidate;

  const { subscribe } = options;

  useEffect(() => {
    if (subscribe === null) return;

    const services = getServices();
    let cancelled = false;
    let poll: ReturnType<typeof setInterval> | undefined;
    let previous: readonly VacationRequest[] | null = null;

    const refresh = (): void => {
      if (cancelled) return;
      for (const key of invalidate.current) void queryClient.invalidateQueries({ queryKey: key });
    };

    const unsubscribe = subscribe(
      (requests) => {
        if (cancelled) return;

        const changes = diffRequests(previous, requests);
        const first = previous === null;
        previous = requests;

        // The first delivery is the state the screen already has.
        if (first) return;
        /*
         * `left` counts. A subscription filtered by status loses a request when
         * it is cancelled or decided, and treating that as "nothing happened"
         * is what left a cancelled request on a leader's board until somebody
         * reloaded.
         */
        if (
          changes.arrived.length === 0 &&
          changes.moved.length === 0 &&
          changes.left.length === 0
        ) {
          return;
        }

        onChanges.current?.(changes);
        refresh();
      },
      (error) => {
        if (cancelled) return;
        services.logger.warn('live requests unavailable; falling back to polling', { error });
        if (poll === undefined) poll = setInterval(refresh, POLL_MS);
      },
    );

    return () => {
      cancelled = true;
      if (poll !== undefined) clearInterval(poll);
      unsubscribe();
    };
  }, [subscribe, queryClient]);
};
